import {faReceipt} from '@fortawesome/free-solid-svg-icons/faReceipt';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import filesize from 'filesize';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {Col, Row} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import styled from 'styled-components';
import {dateFormatFR} from '../../../utils';
import DataTable from '../../../common/components/Datatable/Datatable';
import Screen from '../../../common/components/layout/Screen';
import Metadata from '../../../common/components/Metadata';
import {FILTER_EXPRESSION_OPERATOR, FILTER_OPERATOR, ORDER_DIRECTION} from '../../../common/constants';
import {GRAPHQL_QUERY_RECEIPTS} from '../../../common/queries/receipts';
import TicketRowAction from './TicketRowActionAdmin';
import DateRangePicker from '../../../common/components/Dates/DateRangePicker';
import SelectMerchant from '../../../common/components/SelectMerchant';
import DisplayBoolean from '../../../common/components/DisplayBoolean';
import SelectReceiptIsPro from '../../../common/components/SelectReceiptIsPro';

const Span = styled.span`
  font-family: 'Segoe UI Bold', sans-serif;
`;

const SelectMerchantContainer = styled.div`
   margin: 5px;
   width: 250px;
`;

const MargedDiv = styled.div`
  margin: 5px;
`;

/**
 * This is used to conditionally display receipt.mobile_phone or receipt.user.mobile_phone
 */
const DisplayMobilePhone = ({cell}) => {
  if (cell.row && cell.row.original) {

    const row = cell.row.original;

    if (row.user && row.user.mobilePhone) {
      return <>{row.user.mobilePhone}</>;
    }
    else if (row.mobilePhone) {
      return <>{row.mobilePhone}</>;
    }
  }

  return <></>;
};

const columns = t => [
  {
    Header: '#',
    accessor: 'id',
    sortable: true,
  },
  {
    Header: t('dataTable.merchantBusinessName'),
    accessor: 'merchant.businessName',
    sortable: true,
  },
  {
    Header: t('brand'),
    accessor: 'brand.name',
    sortable: true,
  },
  {
    Header: t('dataTable.email'),
    accessor: 'user.email',
    sortable: true,
  },
  {
    Header: t('dataTable.mobilePhone'),
    Cell: ({cell}) => <DisplayMobilePhone cell={cell}/>,
    accessor: 'user.mobilePhone',
    sortable: true,
  },
  {
    Header: t('dataTable.isPro'),
    Cell: ({cell}) => <DisplayBoolean bool={cell.value}/>,
    accessor: 'isPro',
    sortable: true,
  },
  {
    Header: t('dataTable.creation'),
    Cell: ({cell}) => dateFormatFR({date: cell.value}),
    accessor: 'createdAt',
    sortable: true,
  },
  {
    Header: t('dataTable.fileSize'),
    Cell: ({cell}) => filesize(cell.value),
    accessor: 'file.size',
    sortable: true,
  },
  {
    Header: t('dataTable.detail'),
    Cell: ({cell}) => <TicketRowAction receipt={cell.value}/>,
    accessor: row => row,
  },
];

const createFilters = (search, merchantFilter, startDate, endDate, isPro) => {
  const andFilters = [];

  const startObject = {
    field: 'receipt.createdAt',
    operator: FILTER_OPERATOR.GreaterThan,
    value: startDate,
  };

  const endObject = {
    field: 'receipt.createdAt',
    operator: FILTER_OPERATOR.LessThan,
    value: endDate,
  };

  const merchantFilterObject = {
    field: 'receipt.merchant.id',
    operator: FILTER_OPERATOR.Equal,
    value: merchantFilter,
  };

  const isProFilterObject = {
    field: 'receipt.isPro',
    operator: FILTER_OPERATOR.Equal,
    value: isPro,
  };


  if (startDate) {
    andFilters.push(startObject);
  }

  if (endDate) {
    andFilters.push(endObject);
  }

  if (merchantFilter) {
    andFilters.push(merchantFilterObject);
  }

  if (isPro !== 'default' && isPro !== 'all') {
    andFilters.push(isProFilterObject);
  }


  return {
    operator: FILTER_EXPRESSION_OPERATOR.And,
    expressions:[
      {
        operator: FILTER_EXPRESSION_OPERATOR.And,
        filters: andFilters,
      },
      {
        operator: FILTER_EXPRESSION_OPERATOR.Or,
        filters: [{
          field: 'receipt.id',
          operator: FILTER_OPERATOR.Like,
          value: `%${search}%`,
        }, {
          field: 'user.email',
          operator: FILTER_OPERATOR.Like,
          value: `%${search}%`,
        }, {
          field: 'user.mobilePhone',
          operator: FILTER_OPERATOR.Like,
          value: `%${search}%`,
        }, {
          field: 'receipt.mobilePhone',
          operator: FILTER_OPERATOR.Like,
          value: `%${search}%`,
        }, {
          field: 'merchant.businessName',
          operator: FILTER_OPERATOR.Like,
          value: `%${search}%`,
        }, {
          field: 'brand.name',
          operator: FILTER_OPERATOR.Like,
          value: `%${search}%`,
        }],
      },
    ],
    relations: ['user', 'brand', 'merchant', 'file'],
  };
};

const TicketViewAdmin = props => {
  const {t} = props;
  const [merchantFilter, setMerchantFilter] = useState('');
  const [{startDate, endDate}, setDateFilter] = useState({});
  const [search, setSearch] = useState('');
  const [isProFilter, setIsProFilter] = useState('default');


  const additionalFilters = (
    <>
      <MargedDiv>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onDatesChange={({startDate, endDate}) => setDateFilter({startDate, endDate})}/>
      </MargedDiv>
      <SelectMerchantContainer>
        <SelectMerchant
          placeholder={t('merchantFilter')}
          lightStyles={true}
          onChange={option => {
            if (option && option.value) {
              setMerchantFilter(option.value);
            } else {
              setMerchantFilter('');
            }
          }}
          isClearable={true}/>
      </SelectMerchantContainer>
      <MargedDiv>
        <SelectReceiptIsPro
          value={isProFilter}
          onChange={e => {
            setIsProFilter(e.target.value);
          }}
        />
      </MargedDiv>
    </>
  );

  return (
    <Screen
      header={<Metadata title={t('title')} meta={t('meta')}/>}
      leftTitle={
        <>
          <FontAwesomeIcon icon={faReceipt} color={'rgb(201,0,95)'}/>
          <Span className="route-name">{t('tickets')}</Span>
        </>}>
      <Row>
        <Col xs={12}>
          <DataTable
            query={GRAPHQL_QUERY_RECEIPTS}
            columns={columns(t)}
            keyResult="receipts"
            defaultSort={{field: 'createdAt', direction: ORDER_DIRECTION.DESC}}
            onSearch={value => setSearch(value)}
            advancedFilters={createFilters(search, merchantFilter, startDate, endDate, isProFilter)}
            additionalFilters={additionalFilters}
          />
        </Col>
      </Row>
    </Screen>
  );
};

TicketViewAdmin.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation('ticket')(TicketViewAdmin);
